import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { UserPlusIcon, UsersIcon, BuildingStorefrontIcon } from '@heroicons/react/24/outline';
import AddContactModal from './AddContactModal';
import DataTable from '../common/DataTable';
import { contactsService } from '../../services/contactsService';
import { Tab } from '@headlessui/react';
import StatCard from '../common/StatCard';
import { 
  CurrencyRupeeIcon, 
  UserGroupIcon, 
  ShoppingBagIcon,
  ArchiveBoxIcon
} from '@heroicons/react/24/outline';

function ContactsLayout({ children }) {
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleContactAdded = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const tabs = [
    { name: 'Customers', href: '/contacts/customers', icon: UsersIcon },
    { name: 'Suppliers', href: '/contacts/suppliers', icon: BuildingStorefrontIcon }
  ];

  const contactType = location.pathname.includes('customers') ? 'Customer' : 'Supplier';

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Contacts</h1>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
            Manage your customers and suppliers
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium 
              rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <UserPlusIcon className="h-5 w-5 mr-2" />
            Add New Contact
          </button>
        </div>
      </div>

      <div className="mt-8">
        <Tab.Group selectedIndex={location.pathname.includes('suppliers') ? 1 : 0}>
          <Tab.List className="flex flex-wrap gap-2">
            {tabs.map((tab) => (
              <Tab
                key={tab.name}
                as={Link}
                to={tab.href}
                className={({ selected }) => `
                  ${selected
                    ? 'bg-primary-600 text-white dark:bg-primary-500 dark:text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                  } 
                  rounded-full px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-700 
                  transition-colors duration-150 ease-in-out flex items-center space-x-2
                `}
              >
                <tab.icon className="h-5 w-5" />
                <span>{tab.name}</span>
              </Tab>
            ))}
          </Tab.List>

          <div className="mt-4">
            <Routes>
              <Route path="/" element={<Navigate to="/contacts/customers" replace />} />
              
              <Route path="customers" element={<CustomersList key={refreshTrigger} />} />
              <Route path="suppliers" element={<SuppliersList key={refreshTrigger} />} />
            </Routes>
          </div>
        </Tab.Group>
      </div>

      <AddContactModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        contactType={contactType}
        onContactAdded={handleContactAdded}
      />
    </div>
  );
}

function CustomersList() {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [stats, setStats] = useState({
    totalBalance: 0,
    totalCustomers: 0,
    totalCratesOut: 0,
    activeCustomers: 0
  });

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const contacts = await contactsService.getContacts('customer');
      setData(contacts);
      
      // Calculate stats
      const totalBalance = contacts.reduce((sum, contact) => sum + (contact.balance || 0), 0);
      const totalCratesOut = contacts.reduce((sum, contact) => sum + (contact.cratesOut || 0), 0);
      setStats({
        totalBalance,
        totalCustomers: contacts.length,
        totalCratesOut,
        activeCustomers: contacts.filter(c => c.balance !== 0 || c.cratesOut > 0).length
      });
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const handleViewContact = (contact) => {
    setSelectedContact(contact);
    setIsViewModalOpen(true);
  };

  const columns = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: (info) => (
        <div className="font-medium text-gray-900 dark:text-white">
          {info.getValue()}
        </div>
      ),
    },
    {
      accessorKey: 'cratesOut',
      header: 'Crates Out',
    },
    {
      accessorKey: 'balance',
      header: 'Balance',
      cell: (info) => `₹${info.getValue().toLocaleString()}`,
    },
    {
      accessorKey: 'phone',
      header: 'Phone',
    },
    {
      id: 'actions',
      header: '',
      cell: (info) => (
        <button 
          onClick={() => navigate(`/contacts/customer/${info.row.original.id}`)}
          className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
        >
          View Details
        </button>
      ),
    }
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Outstanding"
          value={`₹${stats.totalBalance.toLocaleString()}`}
          icon={CurrencyRupeeIcon}
          iconBgColor="bg-red-500"
        />
        <StatCard
          title="Total Customers"
          value={stats.totalCustomers}
          icon={UserGroupIcon}
          iconBgColor="bg-blue-500"
        />
        <StatCard
          title="Total Crates Out"
          value={stats.totalCratesOut}
          icon={ArchiveBoxIcon}
          iconBgColor="bg-yellow-500"
        />
        <StatCard
          title="Active Customers"
          value={stats.activeCustomers}
          icon={ShoppingBagIcon}
          iconBgColor="bg-green-500"
        />
      </div>

      <DataTable
        data={data}
        columns={columns}
        sorting={sorting}
        setSorting={setSorting}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        searchPlaceholder="Search customers..."
      />
    </div>
  );
}

function SuppliersList() {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalBalance: 0,
    totalSuppliers: 0,
    activePurchases: 0
  });

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const contacts = await contactsService.getContacts('supplier');
      setData(contacts);
      
      // Calculate stats
      const totalBalance = contacts.reduce((sum, contact) => sum + (contact.balance || 0), 0);
      setStats({
        totalBalance,
        totalSuppliers: contacts.length,
        activePurchases: contacts.filter(c => c.balance > 0).length
      });
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const columns = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: (info) => (
        <div className="font-medium text-gray-900 dark:text-white">
          {info.getValue()}
        </div>
      ),
    },
    {
      accessorKey: 'totalPaid',
      header: 'Total Paid',
      cell: (info) => (
        <div className="text-gray-900 dark:text-white">
          ₹{(info.getValue() || 0).toLocaleString()}
        </div>
      ),
    },
    {
      accessorKey: 'balance',
      header: 'Outstanding',
      cell: (info) => (
        <div className={`font-medium ${
          info.getValue() > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
        }`}>
          ₹{(info.getValue() || 0).toLocaleString()}
        </div>
      ),
    },
    {
      accessorKey: 'phone',
      header: 'Phone',
    },
    {
      id: 'actions',
      header: '',
      cell: (info) => (
        <button 
          onClick={() => navigate(`/contacts/supplier/${info.row.original.id}`)}
          className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
        >
          View Details
        </button>
      ),
    }
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          title="Total Outstanding"
          value={`₹${stats.totalBalance.toLocaleString()}`}
          icon={CurrencyRupeeIcon}
          iconBgColor="bg-red-500"
        />
        <StatCard
          title="Total Suppliers"
          value={stats.totalSuppliers}
          icon={UserGroupIcon}
          iconBgColor="bg-blue-500"
        />
        <StatCard
          title="Active Purchases"
          value={stats.activePurchases}
          icon={ShoppingBagIcon}
          iconBgColor="bg-green-500"
        />
      </div>

      <DataTable
        data={data}
        columns={columns}
        sorting={sorting}
        setSorting={setSorting}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        searchPlaceholder="Search suppliers..."
      />
    </div>
  );
}

export default ContactsLayout;
